import React, { FC, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { toast } from "react-toastify";
import { resetPassword } from "services/auth";
import BackgroundImg from "images/login-background.jpg";

export interface PageResetPasswordProps {
  className?: string;
}

const PageResetPassword: FC<PageResetPasswordProps> = ({ className = "" }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      toast.error("Reset token is missing.");
      navigate("/login");
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setIsSubmitting(true);
      await resetPassword(token, password);
      toast.success("Your password has been reset. You can now log in.");
      navigate("/login");
    } catch (err: any) {
      toast.error(err.message || "Reset failed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`nc-PageResetPassword min-h-[calc(100vh-5rem)] flex justify-center items-center bg-neutral-50 dark:bg-neutral-900 ${className}`}>
      <Helmet>
        <title>Reset Password || Torch Events</title>
      </Helmet>

      <div className="my-4 flex flex-col mx-4 px-4 sm:my-8 shadow-[0_10px_34px_-15px_rgba(0,0,0,0.24)] rounded sm:w-1/2 md:w-1/2 lg:w-1/3 bg-white dark:bg-neutral-800">
      <h2 className="text-black text-xl font-semibold text-center mt-5">Set a New Password</h2>
        <div
            className="w-full min-h-[200px] p-4 bg-contain bg-no-repeat bg-center text-white rounded-t sm:rounded-l sm:rounded-tr-none"
            style={{ backgroundImage: `url(${BackgroundImg})` }}
        />

        <div className="mx-auto w-full space-y-6 py-4 px-8">
          <form onSubmit={handleSubmit} className="grid gap-6">
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">New Password</span>
              <Input
                type="password"
                placeholder="Enter new password"
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">Confirm Password</span>
              <Input
                type="password"
                placeholder="Re-enter password"
                className="mt-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </label>
            <ButtonPrimary type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </ButtonPrimary>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PageResetPassword;
